import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LuTriangleAlert, LuRefreshCw } from "react-icons/lu";
import { api, ApiError, getStoredToken } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Console auth gate.
 *
 * Behaviour:
 *   - No token in localStorage  -> bounce to /login (need to sign in)
 *   - Token + /auth/me returns 401 with token-bad error code -> bounce
 *     to /login (token already cleared by the api client)
 *   - Token + /auth/me returns any OTHER error (server down, transient
 *     500, network blip on dev worker restart) -> show a "reconnecting"
 *     screen with a Retry button and KEEP THE TOKEN. Refresh once the
 *     worker is back up.
 *   - 200 -> render the app.
 */
export function ConsoleAuthGuard({ children }: { children: React.ReactNode }) {
  const hasToken = Boolean(getStoredToken());

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["console", "auth", "me"],
    queryFn: () => api.auth.me(),
    retry: false,
    enabled: hasToken,
    // Treat the auth check as "good for 30s" so a flapping worker
    // doesn't force a re-fetch on every page navigation.
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error instanceof ApiError && error.status !== 401) {
      console.error("[console] auth check failed:", error);
    }
  }, [isError, error]);

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return <div className="h-full bg-[var(--color-bg)]" />;
  }

  // Transient failure: KEEP the token, show reconnecting UI. Only force
  // sign-out when the api client already cleared the token (api.ts
  // does this for INVALID_SESSION / NO_SESSION / NOT_AN_OPERATOR).
  if (isError) {
    if (!getStoredToken()) {
      return <Navigate to="/login" replace />;
    }
    const status = error instanceof ApiError ? error.status : null;
    return (
      <div className="flex min-h-screen items-center justify-center p-6 bg-[var(--color-bg)]">
        <div className="max-w-sm w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center space-y-3">
          <div className="w-9 h-9 rounded-md bg-[rgba(250,176,5,0.1)] border border-[rgba(250,176,5,0.3)] flex items-center justify-center mx-auto">
            <LuTriangleAlert className="w-4 h-4 text-[#fcd34d]" />
          </div>
          <p className="text-sm font-medium text-[var(--color-text)]">
            Console couldn't reach the API
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            {status
              ? `Last response was HTTP ${status}.`
              : "Network unreachable."}{" "}
            Your session is preserved. Retry once the worker is back.
          </p>
          <Button
            onClick={() => refetch()}
            disabled={isFetching}
            className="w-full"
          >
            <LuRefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
            {isFetching ? "Retrying..." : "Retry"}
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
